import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { hasActiveUploads } from "../services/audioProcessingService";

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const WARNING_BEFORE_MS = 2 * 60 * 1000; // Show warning 2 minutes before timeout
const ACTIVITY_CHECK_INTERVAL_MS = 30 * 1000; // Check every 30 seconds
// Keep-alive cadence while uploads are in flight. S3 uploads go direct from
// the browser, so the server sees no traffic and its lastActivity would
// otherwise age out. Pinging every 5 min stays well under the 30-min budget.
const UPLOAD_KEEPALIVE_INTERVAL_MS = 5 * 60 * 1000;

interface UseSessionTimeoutOptions {
  enabled: boolean;
  onTimeout: () => void;
}

export function useSessionTimeout({
  enabled,
  onTimeout,
}: UseSessionTimeoutOptions) {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const lastActivityRef = useRef(Date.now());
  const lastUploadKeepAliveRef = useRef(0);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (showWarning) {
      setShowWarning(false);
    }
  }, [showWarning]);

  const extendSession = useCallback(async () => {
    try {
      // Ping the server to reset server-side lastActivity
      await axios.get("/api/auth/user");
      updateActivity();
      setShowWarning(false);
    } catch {
      // If the ping fails, session may already be expired
      onTimeout();
    }
  }, [onTimeout, updateActivity]);

  useEffect(() => {
    if (!enabled) return;

    const events = ["mousedown", "keydown", "scroll", "touchstart"];

    const handleActivity = () => {
      lastActivityRef.current = Date.now();
    };

    events.forEach((event) => window.addEventListener(event, handleActivity));

    // Check inactivity periodically
    warningTimerRef.current = setInterval(() => {
      // Pause the inactivity timer while audio uploads are in flight. Slow
      // uploads can exceed the 30-min window with no user interaction, and
      // the uploads stream directly to S3 so the server-side lastActivity
      // also ages — ping /api/auth/user periodically to keep it fresh.
      if (hasActiveUploads()) {
        lastActivityRef.current = Date.now();
        setShowWarning(false);
        const now = Date.now();
        if (
          now - lastUploadKeepAliveRef.current >=
          UPLOAD_KEEPALIVE_INTERVAL_MS
        ) {
          lastUploadKeepAliveRef.current = now;
          axios.get("/api/auth/user").catch(() => {});
        }
        return;
      }

      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = INACTIVITY_TIMEOUT_MS - elapsed;

      if (remaining <= 0) {
        // Timed out
        setShowWarning(false);
        onTimeout();
      } else if (remaining <= WARNING_BEFORE_MS) {
        // Show warning
        setShowWarning(true);
        setSecondsRemaining(Math.ceil(remaining / 1000));
      } else {
        setShowWarning(false);
      }
    }, ACTIVITY_CHECK_INTERVAL_MS);

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, handleActivity)
      );
      if (warningTimerRef.current) clearInterval(warningTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [enabled, onTimeout]);

  // Countdown timer when warning is visible
  useEffect(() => {
    if (showWarning) {
      countdownRef.current = setInterval(() => {
        const elapsed = Date.now() - lastActivityRef.current;
        const remaining = INACTIVITY_TIMEOUT_MS - elapsed;
        if (remaining <= 0) {
          setShowWarning(false);
          onTimeout();
        } else {
          setSecondsRemaining(Math.ceil(remaining / 1000));
        }
      }, 1000);
    } else {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    }

    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [showWarning, onTimeout]);

  return {
    showWarning,
    secondsRemaining,
    extendSession,
    dismissWarning: () => setShowWarning(false),
  };
}
